// Performance utility functions

/**
 * Debounce function to limit how often a function is called.
 * Supports leading edge and cancel.
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options?: { leading?: boolean }
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeout: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;
  
  const executedFunction = (...args: Parameters<T>) => {
    lastArgs = args;
    
    if (options?.leading && !timeout) {
      func(...args);
    }
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      timeout = null;
      if (!options?.leading && lastArgs) {
        func(...lastArgs);
      }
      lastArgs = null;
    }, wait);
  };
  
  executedFunction.cancel = () => {
    if (timeout) clearTimeout(timeout);
    timeout = null;
    lastArgs = null;
  };
  
  return executedFunction;
}

/**
 * Throttle function to ensure function is called at most once per interval
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Lazy load images with intersection observer
 */
export function lazyLoadImage(img: HTMLImageElement): void {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const target = entry.target as HTMLImageElement;
        const src = target.dataset.src;
        if (src) {
          target.src = src;
          target.removeAttribute('data-src');
        }
        observer.unobserve(target);
      }
    });
  }, {
    rootMargin: '50px'
  });
  
  observer.observe(img);
}

/**
 * Process files in chunks to avoid blocking main thread
 */
export async function processFileInChunks(
  file: File,
  chunkSize: number = 1024 * 1024 // 1MB chunks
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      resolve(event.target?.result as string);
    };
    
    reader.onerror = reject;
    
    // For small files, read directly
    if (file.size < chunkSize) {
      reader.readAsDataURL(file);
    } else {
      // For larger files, use blob slice
      reader.readAsDataURL(file);
    }
  });
}

/**
 * Optimize localStorage operations with batching
 */
export class LocalStorageBatcher {
  private pending: Map<string, any> = new Map();
  private timeoutId: NodeJS.Timeout | null = null;
  private readonly delay: number;

  constructor(delay: number = 100) {
    this.delay = delay;
  }

  set(key: string, value: any): void {
    this.pending.set(key, value);
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.delay);
  }

  flush(): void {
    this.pending.forEach((value, key) => {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        console.error(`Failed to save ${key} to localStorage:`, e);
      }
    });
    this.pending.clear();
    this.timeoutId = null;
  }
}

/**
 * Memoize expensive computations
 */
export function memoize<T extends (...args: any[]) => any>(fn: T): T {
  const cache = new Map();
  
  return ((...args: any[]) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = fn(...args);
    cache.set(key, result);
    return result;
  }) as T;
}

/**
 * Check if device is mobile
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Get optimal chunk size based on device
 */
export function getOptimalChunkSize(): number {
  const isMobile = isMobileDevice();
  return isMobile ? 512 * 1024 : 2 * 1024 * 1024; // 512KB for mobile, 2MB for desktop
}

/**
 * Request idle callback polyfill
 */
export function requestIdleCallback(callback: () => void): number {
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(callback);
  }
  return setTimeout(callback, 1) as unknown as number;
}

/**
 * Cancel idle callback polyfill
 */
export function cancelIdleCallback(id: number): void {
  if ('cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
}

/**
 * Compress image before upload
 */
export async function compressImage(
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1080,
  quality: number = 0.8
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressed = canvas.toDataURL(file.type, quality);
        resolve(compressed);
      };
      
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ==================== MEMORY MANAGEMENT ====================

/**
 * Detect memory pressure and trigger cleanup when needed.
 * Works on Chrome (performance.memory) and falls back to heuristics.
 */
export function detectMemoryPressure(): { isUnderPressure: boolean; usagePercent: number } {
  const perf = performance as any;
  if (perf.memory) {
    const { usedJSHeapSize, jsHeapSizeLimit } = perf.memory;
    const usagePercent = usedJSHeapSize / jsHeapSizeLimit;
    return { isUnderPressure: usagePercent > 0.85, usagePercent };
  }
  // Heuristic: if localStorage is large, assume moderate pressure
  let totalSize = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) totalSize += (localStorage.getItem(key) || '').length;
  }
  const usagePercent = totalSize / (5 * 1024 * 1024); // 5MB typical limit
  return { isUnderPressure: usagePercent > 0.8, usagePercent };
}

/**
 * Clean up stale localStorage entries to free memory.
 * Removes entries older than maxAge that match the given prefix.
 */
export function cleanupLocalStorage(
  prefixes: string[],
  maxAge: number = 7 * 24 * 60 * 60 * 1000 // 7 days
): number {
  let removed = 0;
  const now = Date.now();
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const matchesPrefix = prefixes.some(p => key.startsWith(p));
    if (!matchesPrefix) continue;

    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const data = JSON.parse(raw);
      // Check for timestamp fields
      const ts = data.lastShown || data.timestamp || data.lastModified || data.createdAt;
      if (ts && typeof ts === 'number' && (now - ts) > maxAge) {
        keysToRemove.push(key);
      }
    } catch {
      // If we can't parse it and it's large, remove it
      const raw = localStorage.getItem(key);
      if (raw && raw.length > 50000) keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(k => { localStorage.removeItem(k); removed++; });
  return removed;
}

// ==================== RENDER OPTIMIZATION ====================

/**
 * Batch multiple state updates into a single render cycle.
 * Uses microtask scheduling to coalesce updates.
 */
export class RenderScheduler {
  private pending: Array<() => void> = [];
  private scheduled = false;

  schedule(update: () => void): void {
    this.pending.push(update);
    if (!this.scheduled) {
      this.scheduled = true;
      queueMicrotask(() => this.flush());
    }
  }

  private flush(): void {
    const updates = [...this.pending];
    this.pending = [];
    this.scheduled = false;
    updates.forEach(fn => fn());
  }
}

export const renderScheduler = new RenderScheduler();

// ==================== NETWORK-AWARE LOADING ====================

type ConnectionEffectiveType = '4g' | '3g' | '2g' | 'slow-2g';

/**
 * Get the effective network type. Falls back to '4g' if unavailable.
 */
export function getEffectiveConnectionType(): ConnectionEffectiveType {
  const conn = (navigator as any).connection;
  return conn?.effectiveType || '4g';
}

/**
 * Check if the user is on a slow connection.
 */
export function isSlowConnection(): boolean {
  const type = getEffectiveConnectionType();
  return type === '2g' || type === 'slow-2g';
}

/**
 * Conditionally load a resource based on network quality.
 * On slow connections, returns null (skip loading).
 */
export function networkAwareLoad<T>(
  loader: () => Promise<T>,
  fallback?: T
): Promise<T | null> {
  if (isSlowConnection() && fallback !== undefined) {
    return Promise.resolve(fallback);
  }
  return loader().catch(() => fallback ?? null);
}

// ==================== SMART PRELOADING ====================

/**
 * Preload a list of routes/components during idle time.
 * Only preloads on fast connections.
 */
export function preloadOnIdle(loaders: Array<() => Promise<any>>): void {
  if (isSlowConnection()) return;

  const load = () => {
    loaders.forEach(loader => {
      requestIdleCallback(() => {
        loader().catch(() => { /* silent fail for preloads */ });
      });
    });
  };

  if (document.readyState === 'complete') {
    load();
  } else {
    window.addEventListener('load', load, { once: true });
  }
}

// ==================== FRAME-RATE AWARE ANIMATIONS ====================

/**
 * Run a callback only when the browser is ready to paint.
 * Skips frames if the callback takes too long.
 */
export function onNextFrame(callback: () => void): number {
  return requestAnimationFrame(() => {
    // Use double-rAF to ensure we're past layout/paint
    requestAnimationFrame(callback);
  });
}

/**
 * Measure the time a function takes and log if it exceeds a threshold.
 */
export function measurePerformance<T>(label: string, fn: () => T, thresholdMs: number = 16): T {
  const start = performance.now();
  const result = fn();
  const duration = performance.now() - start;
  if (duration > thresholdMs) {
    console.warn(`[Perf] ${label} took ${duration.toFixed(1)}ms (threshold: ${thresholdMs}ms)`);
  }
  return result;
}

// ==================== DOM OPTIMIZATION ====================

/**
 * Batch DOM reads and writes to avoid layout thrashing.
 */
export class DOMBatcher {
  private reads: Array<() => void> = [];
  private writes: Array<() => void> = [];
  private scheduled = false;

  read(fn: () => void): void {
    this.reads.push(fn);
    this.scheduleFlush();
  }

  write(fn: () => void): void {
    this.writes.push(fn);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      // Reads first, then writes
      const reads = [...this.reads];
      const writes = [...this.writes];
      this.reads = [];
      this.writes = [];
      this.scheduled = false;
      reads.forEach(fn => fn());
      writes.forEach(fn => fn());
    });
  }
}

export const domBatcher = new DOMBatcher();

// ==================== EVENT LISTENER OPTIMIZATION ====================

/**
 * Create a passive event listener that auto-cleans up.
 * Returns a cleanup function.
 */
export function addPassiveListener(
  target: EventTarget,
  event: string,
  handler: EventListener
): () => void {
  target.addEventListener(event, handler, { passive: true });
  return () => target.removeEventListener(event, handler);
}

/**
 * Visibility-aware interval: pauses when tab is hidden.
 */
export function createVisibilityAwareInterval(
  callback: () => void,
  intervalMs: number
): () => void {
  let id: ReturnType<typeof setInterval> | null = null;

  const start = () => {
    if (id) return;
    id = setInterval(callback, intervalMs);
  };

  const stop = () => {
    if (id) { clearInterval(id); id = null; }
  };

  const onVisibilityChange = () => {
    if (document.hidden) stop(); else start();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (!document.hidden) start();

  return () => {
    stop();
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
